import React from "react";

/**
 * Shared Leaderboard Component
 * Shows top players and highlights current user's position
 */
const Leaderboard = ({ 
  leaderboard, 
  currentUserId = null, 
  title = "🏆 Resultatliste 🏆",
  emptyMessage = "Ingen spillere ennå. Vær den første!",
  scoreLabel = "totalScore", // or "score" for day leaderboard
  showCount = 10,
}) => {
  if (!leaderboard || leaderboard.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-lg p-6 mt-6 max-w-2xl mx-auto">
        <h2 className="text-2xl font-bold text-center mb-4 text-gray-800">
          {title}
        </h2>
        <p className="text-center text-gray-600">{emptyMessage}</p>
      </div>
    );
  }

  // Find current user's position in the full leaderboard
  const userIndex = currentUserId 
    ? leaderboard.findIndex(p => p.userId === currentUserId)
    : -1;
  // Get top 10 and check if user is in it
  const topPlayers = leaderboard.slice(0, showCount);
  const userInTopPlayers = userIndex !== -1 && userIndex < showCount;

  // Get user's entry if they're outside top 10
  const userEntry = !userInTopPlayers && userIndex !== -1 
    ? leaderboard[userIndex] 
    : null;

  const renderPlayerRow = (player, index, isUserRow = false) => {
    const isCurrentUser = player.userId === currentUserId;
    const displayIndex = isUserRow ? userIndex : index;
    
    return (
      <div
        key={player.userId}
        className={`flex justify-between items-center p-3 rounded-lg ${
          displayIndex === 0
            ? "bg-yellow-100 border-2 border-yellow-400"
            : displayIndex === 1
            ? "bg-gray-100 border-2 border-gray-400"
            : displayIndex === 2
            ? "bg-orange-100 border-2 border-orange-400"
            : "bg-gray-50"
        } ${
          isCurrentUser
            ? "ring-2 ring-advent-primary-light"
            : ""
        }`}
      >
        <div className="flex items-center gap-3">
          <span className="text-xl font-bold text-gray-700 w-8">
            {displayIndex === 0
              ? "🥇"
              : displayIndex === 1
              ? "🥈"
              : displayIndex === 2
              ? "🥉"
              : `${displayIndex + 1}.`}
          </span>
          <span className="font-semibold text-gray-800">
            {player.name}
            {isCurrentUser && (
              <span className="ml-2 text-sm text-advent-primary-light">
                (deg)
              </span>
            )}
          </span>
        </div>
        <span className="text-lg font-bold text-gray-800">
          {player[scoreLabel] ?? player.score ?? player.totalScore}
        </span>
      </div>
    );
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-6 mt-6 max-w-2xl mx-auto">
      <h2 className="text-2xl font-bold text-center mb-4 text-gray-800">
        {title}
      </h2>
      <div className="space-y-2">
        {topPlayers.map((player, index) => renderPlayerRow(player, index))}
        
        {/* Show separator and user's position if they're outside top 10 */}
        {userEntry && (
          <>
            <div className="flex items-center gap-2 py-2">
              <div className="flex-1 border-t border-gray-300"></div>
              <span className="text-xs text-gray-500 px-2">Din plassering</span>
              <div className="flex-1 border-t border-gray-300"></div>
            </div>
            {renderPlayerRow(userEntry, userIndex, true)}
          </>
        )}
      </div>
    </div>
  );
};

export default Leaderboard;
